import { Input } from '@cobliteam/ui/form';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';

import AlertFooter from '../components/AlertFooter';
import { CommandDialog, CommandInput, CommandList } from '../components/Command';
import { VirtualizedItem } from '../components/VirtualizedItem';

import ArticleDetails from '../components/entities/Article/ArticleDetails';
import ArticleIADetails from '../components/entities/ArticleIA/ArticleIADetails';
import DeviceDetails from '../components/entities/Device/DeviceDetails';
import DriverDetails from '../components/entities/Driver/DriverDetails';
import VehicleDetails from '../components/entities/Vehicle/VehicleDetails';
import VehicleGroupDetails from '../components/entities/VehicleGroup/VehicleGroupDetails';

import Sidebar from '../containers/Sidebar';
import { VirtualizedList } from '../containers/VirtualizedList';

import Icon from '@cobliteam/ui/icons';
import useSearch from '../hooks/useSearch';
import { EnumCategory, EnumCategoryType, type TCounts } from '../types';

const ITEM_HEIGHT = 46;

const GlobalSearchContent = () => {
  const searchRef = useRef<HTMLInputElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const { isOpen, search, category, counts, selectedItem, handleSearch, setIsOpen, onChangeCategory } = useSearch();

  const configRowVirtualizer = useVirtualizer({
    count: counts[category.id as keyof TCounts],
    getScrollElement: () => parentRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 12,
  });

  const handleEscapeKeyDown = (e: Event) => {
    e.preventDefault();
    if (category.id) {
      onChangeCategory({ categoryId: '', keepSearch: false });
      return;
    }
    setIsOpen(false);
  };

  return (
    <>
      <Input
        className='h-[34px] w-[300px] justify-between'
        loading={false}
        disabled={false}
        autoFocus={isOpen}
        startIcon={<Icon name='search' />}
        placeholder='Procurar veículos, motoristas e etc...'
        onClick={() => setIsOpen(true)}
      />
      {isOpen && (
        <CommandDialog size='lg' open={isOpen} onOpenChange={setIsOpen} shouldFilter={false} onEscapeKeyDown={handleEscapeKeyDown}>
          <CommandInput ref={searchRef} autoFocus placeholder='Procurar veículos, motoristas e etc...' value={search} onValueChange={handleSearch} />
          <div className='grid h-[500px] grid-cols-12'>
            <section className='col-span-3 border-gray-4 border-r'>
              <Sidebar />
            </section>
            {category.type !== EnumCategoryType.IA && (
              <section className='col-span-4 overflow-auto border-gray-4 border-r'>
                <CommandList>
                  <VirtualizedList
                    parentRef={parentRef}
                    rowVirtualizer={configRowVirtualizer}
                    renderRow={(virtualRow) => <VirtualizedItem key={`${virtualRow.index}-${category.id}-${category.loading}`} virtualRow={virtualRow} />}
                  />
                </CommandList>
              </section>
            )}
            {category.type === EnumCategoryType.IA && <ArticleIADetails key={`${selectedItem?.id}-${selectedItem?.category.loading}`} />}
            {category.type === EnumCategoryType.ITEMS && (
              <section className='col-span-5 flex gap-2'>
                {selectedItem?.category.id === EnumCategory.VEHICLES && <VehicleDetails />}
                {selectedItem?.category.id === EnumCategory.DRIVERS && <DriverDetails />}
                {selectedItem?.category.id === EnumCategory.DEVICES && <DeviceDetails />}
                {selectedItem?.category.id === EnumCategory.VEHICLE_GROUPS && <VehicleGroupDetails />}
                {selectedItem?.category.id === EnumCategory.ARTICLES && <ArticleDetails />}
                {selectedItem?.category.id === EnumCategory.IA_ARTICLES && <ArticleIADetails />}
              </section>
            )}
          </div>
          <AlertFooter />
        </CommandDialog>
      )}
    </>
  );
};

export default GlobalSearchContent;
